// WebOTP boot shim — brings up real Erlang/OTP 27.2 (emscripten wasm) in the tab
// and wires its stdio to the shared xterm widget.
//
// See webotp/README.md for the design.
//
// Everything load-bearing here was measured in a real headless Chromium tab on
// the Dream Machine, not inferred; the comments say which.

import PopcornTerm from "./terminal.js";

const RS = "\x1e";

// ---------------------------------------------------------------------------
// stdin — a byte queue the VM pulls from
// ---------------------------------------------------------------------------
// Emscripten's tty calls `Module.stdin` once per byte and interprets the return
// as: a number = that byte; `null` = EOF; `undefined` = EAGAIN. That distinction
// is the whole mechanism. Returning `null` on an empty queue would be a genuine
// end-of-input and would tear down the Erlang read loop permanently — the shell
// would accept exactly one burst of typing and then go deaf. `undefined` means
// "nothing yet, ask again", which is what an idle terminal actually is.
//
// (The default browser handler is `window.prompt()`, which opens a modal on the
// page's main thread. Because every pthread syscall proxies through that thread,
// one 1-byte stdin read during boot deadlocks the entire VM — BROWSER_FINDINGS
// Wall C. Overriding this is mandatory, not an optimisation.)
const stdinQueue = [];
const encoder = new TextEncoder();

function feed(text) {
  for (const byte of encoder.encode(text)) stdinQueue.push(byte);
}

// ---------------------------------------------------------------------------
// stdout — one channel carrying both program output and control frames
// ---------------------------------------------------------------------------
// `Module.print` is line-buffered by emscripten: one call per '\n'. A line that
// starts with RS is a frame from WebOTP.Repl; anything else is the visitor's own
// output (IO.puts, :io.format, a crash report) and streams through verbatim.
// Sharing one channel is what keeps output and results correctly interleaved.
//
// Consequence worth knowing: `IO.write("no newline")` stays in emscripten's line
// buffer until something emits a '\n'. `IO.puts` is unaffected.
const listeners = { ready: null };

function routeStdout(line) {
  if (line.charCodeAt(0) !== 0x1e) {
    PopcornTerm.writeRaw(line + "\n");
    return;
  }

  const tag = line[1];
  const payload = unescapeFrame(line.slice(3));

  switch (tag) {
    case "R":
      PopcornTerm.writeOutput(payload, false);
      break;
    case "E":
      PopcornTerm.writeOutput(payload, true);
      break;
    // The submission so far does not parse yet — an unclosed `do`, heredoc or
    // bracket. Draw a continuation prompt and keep collecting rather than
    // evaluating a fragment. This frame is ALSO what unfreezes the readline, so
    // it is not optional decoration: without it the prompt hangs on line one of
    // every multi-line block.
    case "M":
      PopcornTerm.writeContinuation();
      break;
    case "C":
      applyCompletion(payload);
      break;
    case "N":
      handleNotification(payload);
      break;
    default:
      // Unknown frame types are shown rather than swallowed — a silently dropped
      // frame is the kind of thing that costs an hour to notice.
      PopcornTerm.writeRaw(line + "\n");
  }
}

function unescapeFrame(s) {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && i + 1 < s.length) {
      const next = s[++i];
      out += next === "n" ? "\n" : next;
    } else {
      out += s[i];
    }
  }
  return out;
}

function handleNotification(payload) {
  const info = Object.fromEntries(
    payload
      .split(" ")
      .filter((kv) => kv.includes("="))
      .map((kv) => kv.split("="))
  );

  if (payload.startsWith("ready")) {
    setStatus("runtime", `Erlang/OTP ${info.otp} (erts ${info.erts}) · real BEAM`);
    setStatus("node", NODE_NAME);
    setStatus("network", "sockets · DNS · TLS — stock gen_tcp over an L5 SOCKFS provider");
    setStatus("cluster", "joining the cluster…");
    PopcornTerm.writeLine(
      `Erlang/OTP ${info.otp} [erts ${info.erts}] · Elixir ${info.elixir} · ` +
        `${info.schedulers} scheduler(s) — running entirely in this tab.`,
      "dim"
    );
    PopcornTerm.writeOutput("", false); // first prompt
    listeners.ready && listeners.ready();
    window.__webotpReady = true;
    return;
  }

  // Slice 2b: distribution is live. `peers` is Node.list()'s length — the count a
  // visitor gets from the standard call, not our own bookkeeping.
  if (payload.startsWith("cluster ")) {
    setStatus("node", info.node || NODE_NAME);
    const peers = Number(info.peers || 0);
    setStatus(
      "cluster",
      peers === 0
        ? "clustered: waiting for a peer — open a second tab"
        : `clustered · Node.list() = ${info.nodes.split(",").join(" · ")}`
    );
    window.__webotpCluster = { node: info.node, peers, nodes: info.nodes || "" };
    return;
  }

  if (payload.startsWith("cluster-failed")) {
    setStatus("cluster", "not clustered — " + payload.slice("cluster-failed ".length));
    window.__webotpCluster = { node: NODE_NAME, peers: 0, nodes: "", error: payload };
  }
}

// ---------------------------------------------------------------------------
// this tab's node name
// ---------------------------------------------------------------------------
// Two tabs of the same page are two nodes, and a reloaded tab is a third, so the
// name has to be unique per VM instance and there is nothing about a tab to derive
// one from. The prefix is load-bearing rather than decorative: `popcorn_relay`
// only DIALS peers that share it, so anything else that turns up on the presenter
// is never dialled by a tab (it can still dial in, if it has the cookie).
const NODE_PREFIX = "webotp_";
const PRESENTER = "presenter@127.0.0.1";

function randomNodeName() {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${NODE_PREFIX}${hex}@${PRESENTER.split("@")[1]}`;
}

const NODE_NAME = randomNodeName();

// ---------------------------------------------------------------------------
// tab completion
// ---------------------------------------------------------------------------
// The line as it stood when Tab was pressed. Remembered here rather than read
// back off the widget so this module never touches PopcornTerm's private state.
let completionSubject = "";

function applyCompletion(payload) {
  const [hint, ...candidates] = payload.split("\t");
  if (hint) {
    PopcornTerm.setInput(completionSubject + hint);
  } else if (candidates.length > 1) {
    PopcornTerm.writeCompletions(candidates);
  }
}

// ---------------------------------------------------------------------------
// boot
// ---------------------------------------------------------------------------
// The page is served from BOTH `/` (the canonical URL since 2026-07-26) and
// `/webotp/` (kept working for the probes, verifiers and every doc link). So the
// payload's base is the directory the document itself came from, not a constant
// — a hardcoded "/webotp/" would make the `/` page pull beam.js from the other
// prefix, which works today only by accident of both paths being served.
export function bootWebOTP({ base = new URL("./", location.href).pathname } = {}) {
  if (window.__webotpBooting) return;
  window.__webotpBooting = true;

  PopcornTerm.mount();
  PopcornTerm.setNode("webotp");
  PopcornTerm.onSubmit = (line) => feed(line + "\n");
  PopcornTerm.onComplete = (line) => {
    completionSubject = line;
    feed(RS + "C " + line + "\n");
  };

  const Module = {
    noInitialRun: true,
    // The tab's /etc. Written from the page rather than baked into beam.data so
    // it is the deployment's choice (like a resolver handed out by DHCP) and so a
    // visitor can rewrite it at runtime — `File.write!("/etc/resolv.conf", ...)`
    // followed by `:inet_db.res_update_conf()` re-points the resolver live.
    //
    // This file is not optional. `inet_res` reads it through
    // `inet_db:res_update_conf/0`, which runs at the TOP of `getbyname_tm/3`;
    // when the file is absent that call CLEARS any nameservers set by hand, so
    // `inet:gethostbyname/1` returns {error, nxdomain} while
    // `inet_res:resolve/4` with explicit nameservers works — a genuinely
    // confusing split that cost a debugging cycle to pin down.
    preRun: [
      () => {
        try {
          Module.FS.mkdir("/etc");
        } catch (e) {
          /* already exists */
        }
        Module.FS.writeFile("/etc/resolv.conf", "nameserver 8.8.8.8\nnameserver 1.1.1.1\n");
        Module.FS.writeFile("/etc/hosts", "127.0.0.1 localhost\n");
      },
    ],
    stdin: () => (stdinQueue.length ? stdinQueue.shift() : undefined),
    print: routeStdout,
    // stderr carries emscripten's own noise ("unsupported syscall: setsockopt"
    // fires during boot on every run and is harmless) alongside real VM
    // diagnostics. Keep it off the visitor's terminal but in the console, where
    // a harness can still read it.
    printErr: (text) => console.log("ERL_ERR " + text),
    // ERTS bypasses erlexec, so the emulator is invoked with the flags erlexec
    // would normally synthesise. `-noshell` selects the dumb-terminal `user`
    // process (fd 0/1 directly) rather than OTP 26+'s `prim_tty` NIF path, which
    // has no emscripten backend.
    arguments: [
      "-S", "1:1", "-SDcpu", "1:1", "-SDio", "1", "-A", "0", "--",
      "-root", "/otp", "-bindir", "/otp/erts-15.2/bin", "-progname", "erl", "--",
      "-home", "/home/web_user", "--",
      "-boot", "/otp/releases/27/start_clean", "-noshell",
      // The value is parsed as an Erlang term, hence the embedded quotes.
      "-kernel", "inetrc", '"/otp/etc/erl_inetrc"',
      // Slice 2b — distribution.
      //
      // `-proto_dist popcorn_ws` is read by net_kernel when distribution starts,
      // so it has to be a VM flag: net_kernel:start/2's option map has no
      // proto_dist key. `-no_epmd` is not an optimisation either — without it
      // erl_distribution starts an erl_epmd child, and there is no epmd in a tab.
      "-proto_dist", "popcorn_ws",
      "-no_epmd",
      "-setcookie", "wsdemo",
      "-popcorn_uplink", PRESENTER,
      "-popcorn_node", NODE_NAME,
      "-popcorn_node_prefix", NODE_PREFIX,
      // The L5 sentinel: RFC 5737 TEST-NET-1, recognised by popcorn_sockfs.js as
      // "the presenter's own dist listener". The port travels for symmetry with
      // the other opens and is ignored — the presenter picks the destination.
      "-popcorn_dist_addr", "192.0.2.1",
      "-popcorn_dist_port", "43210",
      // Match the presenter's net_ticktime (scripts/presenter.sh sets 240, for
      // browser tabs whose timers get throttled when backgrounded). Mismatched
      // ticktimes across a cluster produce spurious disconnects.
      "-popcorn_ticktime", "240",
      // This cluster is a hub and spokes, not a full mesh: a tab can reach the
      // presenter and other WebOTP tabs, and nothing else. `global` assumes it
      // can connect to every visible node and starts disconnecting links when it
      // cannot, so it has to be told.
      //
      // The two halves say different things, and Slice 2c measured what each one
      // costs (probes/webotp-cluster-2c):
      //
      //   prevent_overlapping_partitions false — do not tear working links down
      //     to protect an invariant this topology does not have. Load-bearing.
      //
      //   connect_all TRUE — because `false' does not mean "be careful", it means
      //     global's name table never syncs with anyone. `:global.register_name'
      //     still answers `:yes' on every tab, so TWO tabs can own the same name
      //     and neither is told; `:global.sync/0' never returns. That is a silent
      //     wrong answer, which is worse than a loud missing feature. With it
      //     true, all of :global/:pg/Phoenix.PubSub pass 22/22 across tabs and
      //     tab<->presenter, deterministically.
      //
      // The invariant this relies on: the presenter and the WebOTP tabs are the
      // only VISIBLE nodes. Attach anything else (a remsh, an observer) as
      // `-hidden' — global ignores hidden nodes, and a visible node a tab cannot
      // dial makes the cluster churn no matter what the tabs are configured to
      // do, because the disconnect is decided on the OTHER node.
      "-kernel", "connect_all", "true", "prevent_overlapping_partitions", "false",
      "-eval", "application:ensure_all_started(elixir), 'Elixir.WebOTP.Repl':start().",
    ],
    onAbort: (what) => {
      PopcornTerm.writeOutput("VM aborted: " + what, true);
      setStatus("runtime", "aborted");
    },
    locateFile: (path) => base + path,
  };

  window.Module = Module;

  // beam.js is emscripten's classic (non-module) glue and expects a global
  // `Module` to already exist. Loading it via a real <script src> also lets
  // emscripten record its own URL, which is how the pthread workers resolve
  // beam.js/beam.wasm — an inline eval would leave them looking in the wrong
  // place.
  const script = document.createElement("script");
  script.src = base + "beam.js";
  script.onload = () => {
    // callMain must wait for every run dependency: wasm instantiate, the 3.9 MB
    // beam.data preload, and the pthread pool warm-up. onRuntimeInitialized is
    // exactly that point — a timer here would be a race.
    const start = () => {
      try {
        Module.callMain(Module.arguments);
      } catch (e) {
        PopcornTerm.writeOutput("callMain failed: " + e, true);
      }
    };
    const prev = Module.onRuntimeInitialized;
    Module.onRuntimeInitialized = () => {
      prev && prev();
      setTimeout(start, 0);
    };
    if (Module.calledRun) start();
  };
  script.onerror = () => setStatus("runtime", "failed to load beam.js");
  document.head.appendChild(script);
}

function setStatus(id, text) {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

export function onWebOTPReady(fn) {
  if (window.__webotpReady) fn();
  else listeners.ready = fn;
}

// Exposed for the CDP harnesses (webotp_verify.py), which drive the shell the
// same way a visitor does rather than reaching around it.
window.WebOTP = { feed, boot: bootWebOTP, onReady: onWebOTPReady };
